import React, { useMemo, useState } from 'react';
import { Plus, Search, Sparkles } from 'lucide-react';
import { Button, Input, Skeleton } from '@ideoz/ui';
import { ScopeCard, type ScopeCardScope } from './ScopeCard';
import { EmptyState } from '@ideoz/ui/components/molecules/EmptyState';

const ScopeSkeleton = () => (
  <div className="border border-border rounded-xl bg-background p-4 space-y-2">
    <div className="flex items-center justify-between">
      <Skeleton className="h-4 w-48" />
      <div className="flex gap-2">
        <Skeleton className="h-5 w-16 rounded-full" />
        <Skeleton className="h-5 w-20 rounded-full" />
      </div>
    </div>
    <Skeleton className="h-3 w-72" />
  </div>
);

export interface ScopesListSectionProps {
  title: string;
  subtitle?: string;
  scopes: ScopeCardScope[];
  isLoading: boolean;
  onCardClick: (id: string) => void;
  onCardEdit: (scope: ScopeCardScope) => void;
  onCardDelete: (id: string) => void;
  /** Queues an ICE evaluation from the card's ⋯ menu. */
  onCardEvaluate: (id: string) => void;
  /** Opens the ICE details popup for a scored card. */
  onCardOpenIce: (id: string) => void;
  /** Scopes with an evaluation queued or running. */
  scoringScopeIds?: string[];
  emptyMessage?: string;
  onNewScope?: () => void;
  onSuggestScopes?: () => void;
}

export const ScopesListSection: React.FC<ScopesListSectionProps> = ({
  title,
  subtitle,
  scopes,
  isLoading,
  onCardClick,
  onCardEdit,
  onCardDelete,
  onCardEvaluate,
  onCardOpenIce,
  scoringScopeIds = [],
  emptyMessage,
  onNewScope,
  onSuggestScopes,
}) => {
  const [query, setQuery] = useState('');

  const filteredScopes = useMemo(() => {
    const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    const matched =
      words.length === 0
        ? scopes
        : scopes.filter((scope) => {
            const haystack = [scope.title, scope.description ?? ''].join(' ').toLowerCase();
            return words.every((word) => haystack.includes(word));
          });

    return [...matched].sort(
      (a, b) => new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime()
    );
  }, [query, scopes]);

  const resolvedEmpty = emptyMessage ?? `No ${title.toLowerCase()} yet. Add your first one.`;
  const hasSearchQuery = query.trim().length > 0;

  return (
    <>
      <header className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100">{title}</h2>
          {subtitle && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{subtitle}</p>
          )}
        </div>
        <div className="relative w-full max-w-xs shrink-0">
          <Search
            size={16}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <Input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search scopes"
            aria-label="Search scopes"
            className="pl-9"
          />
        </div>
      </header>

      <div className="space-y-3">
        {isLoading ? (
          <>
            <ScopeSkeleton />
            <ScopeSkeleton />
            <ScopeSkeleton />
          </>
        ) : filteredScopes.length === 0 ? (
          <div className="text-center text-gray-400 dark:text-gray-500">
            <p className="text-sm">
              {/* {hasSearchQuery ? 'No scopes match your search.' : resolvedEmpty} */}
              {hasSearchQuery && (
                <EmptyState
                  title="No scopes match your search."
                  description="Try a different filter or clear your search to see everything again."
                  action={
                    <Button variant="secondary" size="sm" onClick={() => setQuery('')}>
                      Clear Search
                    </Button>
                  }
                />
              )}
              {!hasSearchQuery && (
                <EmptyState
                  title="You didn't add any scopes yet"
                  description="Scopes are the units of work ideoz analyses and scores. Create your first one, or let the agent suggest scopes from your files."
                  action={
                    <Button variant="primary" size="sm" onClick={onNewScope}>
                      <Plus className="size-4" />
                      New Scope
                    </Button>
                  }
                  secondaryAction={
                    <Button variant="secondary" size="sm" onClick={onSuggestScopes}>
                      <Sparkles className="size-4" />
                      Add multiple scopes
                    </Button>
                  }
                />
              )}
            </p>
          </div>
        ) : (
          filteredScopes.map((scope) => (
            <ScopeCard
              key={scope.id}
              scope={scope}
              onClick={onCardClick}
              onEdit={onCardEdit}
              onDelete={onCardDelete}
              onEvaluate={onCardEvaluate}
              onOpenIce={onCardOpenIce}
              isScoring={scoringScopeIds.includes(scope.id)}
            />
          ))
        )}
      </div>
    </>
  );
};

export default ScopesListSection;
